// src/components/content_editor/CareBundleBuilder.js

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import authFetch from '../../utils/authFetch';
import {
    Box, Typography, TextField, Button, Paper, Grid,
    FormGroup, FormControlLabel, Checkbox, IconButton, Tooltip,
    Divider, Chip, Autocomplete, List, ListItem, ListItemText,
    Menu, MenuItem, ListItemIcon, CircularProgress, Alert
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';

// --- Helper: Validate UUID format ---
const isValidUUID = (id) => {
    if (!id) return false;
    const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return typeof id === 'string' && regex.test(id);
};

const PARAMETER_MAP = {
    vitals: [
        { param: 'vitals.systolic', desc: 'BP Systolic High', condition: '>', value: '160' },
        { param: 'vitals.systolic', desc: 'BP Systolic Low', condition: '<', value: '90' },
        { param: 'vitals.diastolic', desc: 'BP Diastolic High', condition: '>', value: '100' },
        { param: 'vitals.heart_rate', desc: 'Heart Rate High', condition: '>', value: '110' },
        { param: 'vitals.heart_rate', desc: 'Heart Rate Low', condition: '<', value: '50' },
        { param: 'vitals.oxygen_saturation', desc: 'Oxygen Saturation Low', condition: '<', value: '92' },
    ],
    symptoms_feelings: [
        { param: 'symptoms.pain_level', desc: 'Pain Level High', condition: '>', value: '7' },
    ],
};

const CareBundleBuilder = ({ initialData = null, onUpdate, tenantId, isEditMode = false }) => {
    const def = initialData?.module_definition || {};
    const isEdit = initialData?.id && isEditMode;

    // 1. QUERIES
    const { data: medProtocols = [] } = useQuery({
        queryKey: ['medicationProtocols', tenantId],
        queryFn: () => authFetch('/api/protocols/medication-protocols'),
        enabled: !!tenantId,
    });

    const { data: contentItems = [], isLoading: isLoadingContent } = useQuery({
        queryKey: ['contentItems'],
        queryFn: () => authFetch('/api/content/?status=active&latest_only=true&limit=500'),
    });

    // Filter out System Templates for the Checkbox section
    const systemCheckins = useMemo(() => {
        return contentItems.filter(item => item.item_metadata?.is_system_template === true);
    }, [contentItems]);

    // Filter out everything else for the Autocomplete (Trackers/Docs)
    const libraryItems = useMemo(() => {
        return contentItems.filter(item => !item.item_metadata?.is_system_template);
    }, [contentItems]);

    // 3. STATE VARIABLES
    const [name, setName] = useState('');
    const [selectedCheckinIds, setSelectedCheckinIds] = useState([]);
    const [alertRules, setAlertRules] = useState([]);
    const [selectedMedProtocolIds, setSelectedMedProtocolIds] = useState([]);
    const [selectedLibraryConfigs, setSelectedLibraryConfigs] = useState([]);
    
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [currentItemForMenu, setCurrentItemForMenu] = useState(null);
    const [criticalMedDirectives, setCriticalMedDirectives] = useState([]);

    // Helper to resolve ANY ID (UUID or Key) to a valid UUID *from the current contentItems list*
    const resolveToUUID = (idOrKey) => {
        if (isValidUUID(idOrKey)) return idOrKey;
        
        // Try to find item by Key (legacy support)
        const item = contentItems.find(c => c.key === idOrKey || c.item_metadata?.system_key === idOrKey);
        if (item) return item.id;

        return null;
    };

    // NEW: Effect to populate state from initialData whenever it changes or contentItems loads
    useEffect(() => {
        if (initialData && contentItems.length > 0) {
            const def = initialData.module_definition || {};
            
            // Name
            if (isEditMode) setName(initialData.name);
            else if (initialData.id) setName(`Copy of ${initialData.name}`);
            else setName('');

            // --- SMART HYDRATION ---
            const checkinIds = new Set();
            
            // A. From 'default_content_configs'
            (def.default_content_configs || []).forEach(cfg => {
                const validId = resolveToUUID(cfg.content_item_id);
                if (validId) {
                    const item = contentItems.find(c => c.id === validId);
                    if (item?.item_metadata?.is_system_template) {
                        checkinIds.add(validId);
                    }
                }
            });

            // B. From 'mandated_daily_checkins' (Legacy)
            (def.mandated_daily_checkins || []).forEach(key => {
                const validId = resolveToUUID(key);
                if (validId) checkinIds.add(validId);
            });
            setSelectedCheckinIds(Array.from(checkinIds));

            // 2. Hydrate Library Items
            const libraryConfigs = [];
            (def.default_content_configs || []).forEach(cfg => {
                const validId = resolveToUUID(cfg.content_item_id);
                if (!validId) return;

                const item = contentItems.find(c => c.id === validId);
                if (item && !item.item_metadata?.is_system_template) {
                    libraryConfigs.push({
                        content_item_id: validId, 
                        is_critical: cfg.is_critical || false,
                        require_attestation: cfg.require_attestation || false,
                        frequency: cfg.default_frequency || 'weekly'
                    });
                }
            });
            setSelectedLibraryConfigs(libraryConfigs);

            // 3. Hydrate Medications
            if (def.medication_directives && medProtocols.length > 0) {
                const bundleMedNames = new Set((def.medication_directives || []).map(dir => dir.item_name || dir.med_name));
                const protocolIds = medProtocols
                    .filter(protocol => {
                        const protocolMedName = protocol.module_definition?.medication_directives?.[0]?.item_name; 
                        return protocolMedName && bundleMedNames.has(protocolMedName);
                    })
                    .map(p => p.id);
                setSelectedMedProtocolIds(protocolIds);
                
                const criticalMeds = (def.medication_directives || [])
                    .filter(d => d.is_critical)
                    .map(d => d.item_name || d.med_name);
                setCriticalMedDirectives(criticalMeds);
            }

            // 4. Alert Rules
            setAlertRules(def.alert_rules || []);
        }
    }, [initialData, contentItems, medProtocols, isEditMode]);

    // Auto-generate alert rules based on selected check-ins
    useEffect(() => {
        const selectedKeys = selectedCheckinIds.map(id => {
            const item = contentItems.find(c => c.id === id);
            return item?.key;
        }).filter(Boolean);

        let newRules = alertRules.filter(rule => {
            const isAI = rule.type === 'longitudinal_pattern';
            const paramParent = rule.parameter?.split('.')[0];
            return isAI || selectedKeys.includes(paramParent);
        });

        selectedKeys.forEach(taskKey => {
            (PARAMETER_MAP[taskKey] || []).forEach(defaultRule => {
                if (!newRules.some(r => r.parameter === defaultRule.param && r.condition === defaultRule.condition)) {
                    newRules.push({ id: Math.random(), type: 'realtime_threshold', parameter: defaultRule.param, desc: defaultRule.desc, condition: defaultRule.condition, value: defaultRule.value });
                }
            });
        });
        setAlertRules(newRules);
    }, [selectedCheckinIds, contentItems]);

    // 4. HANDLERS
    const handleCheckinChange = (itemId) => {
        setSelectedCheckinIds(prev => 
            prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
        );
    };

    const handleRuleChange = (id, field, value) => setAlertRules(prev => prev.map(rule => rule.id === id ? { ...rule, [field]: value } : rule));
    const addNewAIPrompt = () => setAlertRules(prev => [...prev, { id: Math.random(), type: 'longitudinal_pattern', ai_prompt: "Patient reports 'awful' mood for 3 consecutive days." }]);
    const deleteRule = (id) => setAlertRules(prev => prev.filter(rule => rule.id !== id));

    const handleMenuOpen = (event, item) => {
        setMenuAnchorEl(event.currentTarget);
        setCurrentItemForMenu(item);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setCurrentItemForMenu(null);
    };
    const handleToggleConfig = (configType) => {
        if (!currentItemForMenu) return;
        setSelectedLibraryConfigs(prev =>
            prev.map(config =>
                config.content_item_id === currentItemForMenu.content_item_id
                    ? { ...config, [configType]: !config[configType] }
                    : config
            )
        );
        handleMenuClose();
    };
    const handleToggleCriticalMed = (directiveName) => {
        setCriticalMedDirectives(prev =>
            prev.includes(directiveName) ? prev.filter(name => name !== directiveName) : [...prev, directiveName]
        );
    };

    const handleSaveBundle = () => {
        console.log("CareBundleBuilder: Starting save process...");
        
        const selectedProtocols = medProtocols.filter(p => selectedMedProtocolIds.includes(p.id));
        const medicationSchedules = selectedProtocols.flatMap(protocol => 
            protocol.module_definition?.medication_directives || []
        );

        // Helper to resolve ANY ID (UUID or Key) to a valid UUID
        const resolveToUUID = (idOrKey) => {
            if (isValidUUID(idOrKey)) return idOrKey;
            // Fallback: Try to find item by Key
            const item = contentItems.find(c => c.key === idOrKey || c.item_metadata?.system_key === idOrKey);
            return item ? item.id : null;
        };

        // 1. Library Items (Docs/Trackers)
        const libraryConfigs = selectedLibraryConfigs.reduce((acc, config) => {
            const resolvedId = resolveToUUID(config.content_item_id);
            
            if (resolvedId && isValidUUID(resolvedId)) {
                const item = contentItems.find(c => c.id === resolvedId);
                if (item) {
                    const isTracker = item.content_type === 'activity_log_request';
                    acc.push({
                        content_item_id: resolvedId, 
                        content_type_hint: item.content_type,
                        default_due_in_days: isTracker ? 0 : 7,
                        default_frequency: config.frequency || (isTracker ? 'daily' : 'weekly'),
                        is_critical: config.is_critical || false,
                        require_attestation: config.require_attestation || false
                    });
                }
            } else {
                console.warn("CareBundleBuilder: Dropping Invalid Library Config:", config);
            }
            return acc;
        }, []);

        // 2. System Checkins (Vitals, Mood)
        const systemConfigs = selectedCheckinIds.reduce((acc, idOrKey) => {
            const resolvedId = resolveToUUID(idOrKey);

            if (resolvedId && isValidUUID(resolvedId)) {
                const item = contentItems.find(c => c.id === resolvedId);
                if (item) {
                    acc.push({
                        content_item_id: resolvedId, 
                        content_type_hint: item.content_type,
                        default_due_in_days: 0,
                        default_frequency: 'daily', 
                        is_critical: false,
                        require_attestation: false
                    });
                }
            } else {
                console.warn("CareBundleBuilder: Dropping Invalid System ID/Key:", idOrKey);
            }
            return acc;
        }, []);

        // 3. Merge All Configs
        const allContentConfigs = [...libraryConfigs, ...systemConfigs];

        console.log("CareBundleBuilder: Final Payload Count:", allContentConfigs.length);
        console.log("CareBundleBuilder: Final Payload Data:", allContentConfigs);

        // 4. Process Medication Directives
        const medDirectives = medicationSchedules.map(med => ({
            ...med,
            item_name: med.item_name || med.med_name,
            is_critical: criticalMedDirectives.includes(med.item_name), 
        }));

        onUpdate({
            id: isEditMode ? initialData?.id : undefined, 
            name: name,
            description: `Care bundle for ${name}`,
            protocol_type: 'care_bundle',
            module_definition: {
                mandated_daily_checkins: [], 
                custom_trackers: [], 
                alert_rules: alertRules,
                medication_directives: medDirectives,
                default_content_configs: allContentConfigs, 
            }
        });
    };
    

    if (isLoadingContent) return <Box p={3} textAlign="center"><CircularProgress /></Box>;
    
    // --- NEW SAFETY CHECK ---
    if (contentItems.length === 0) {
        return <Box p={3}><Alert severity="warning">Content Library is empty. System Check-ins cannot be loaded.</Alert></Box>;
    }

    return (
        <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Care Bundle Builder</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12}><TextField fullWidth label="Bundle Name" value={name} onChange={e => setName(e.target.value)} helperText="e.g., Post-Op Cataract Surgery - Standard Protocol" /></Grid>
                
                {/* --- 1. Medications --- */}
                <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Assemble Bundle Contents" /></Divider></Grid>
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom>1. Select Medication Protocols</Typography>
                    <Autocomplete
                        multiple
                        options={medProtocols}
                        getOptionLabel={(option) => option.name}
                        value={medProtocols.filter(p => selectedMedProtocolIds.includes(p.id))}
                        onChange={(event, newValue) => setSelectedMedProtocolIds(newValue.map(v => v.id))}
                        renderInput={(params) => <TextField {...params} label="Search Medication Protocols" />}
                    />
                    {selectedMedProtocolIds.length > 0 && (
                        <Paper variant="outlined" sx={{ mt: 2, p: 2, maxHeight: 400, overflow: 'auto' }}>
                            <Typography variant="subtitle2" gutterBottom>Configure Critical Medications</Typography>
                            <List dense>
                                {medProtocols
                                    .filter(p => selectedMedProtocolIds.includes(p.id))
                                    .flatMap(p => p.module_definition?.medication_directives || [])
                                    .map((directive, index) => (
                                        <ListItem key={`${directive.item_name}-${index}`} dense>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={criticalMedDirectives.includes(directive.item_name)}
                                                        onChange={() => handleToggleCriticalMed(directive.item_name)}
                                                        size="small"
                                                    />
                                                }
                                                label={<Typography variant="body2">{directive.item_name}</Typography>}
                                            />
                                        </ListItem>
                                    ))
                                }
                            </List>
                        </Paper>
                    )}
                </Grid>

                {/* --- 2. Content Items & Trackers (Library) --- */}
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom>2. Select Content & Custom Trackers</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{mb:1}}>
                         Select Documents, Questionnaires, or Custom Trackers from the library.
                    </Typography>
                    <Autocomplete
                        multiple
                        options={libraryItems} 
                        getOptionLabel={(option) => {
                            const typeLabel = option.content_type === 'activity_log_request' ? ' [Tracker]' : '';
                            return `${option.title}${typeLabel}`;
                        }}
                        value={libraryItems.filter(c => selectedLibraryConfigs.some(cfg => cfg.content_item_id === c.id))}
                        onChange={(event, newValue) => {
                            const newConfigs = newValue.map(v => {
                                const existing = selectedLibraryConfigs.find(cfg => cfg.content_item_id === v.id);
                                const isTracker = v.content_type === 'activity_log_request';
                                
                                return existing || { 
                                    content_item_id: v.id, 
                                    is_critical: false, 
                                    require_attestation: false,
                                    frequency: isTracker ? 'daily' : 'weekly' 
                                };
                            });
                            setSelectedLibraryConfigs(newConfigs);
                        }}
                        renderInput={(params) => <TextField {...params} label="Search Library" helperText="Excludes standard system check-ins" />}
                    />
                    {selectedLibraryConfigs.length > 0 && (
                        <Paper variant="outlined" sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
                             <List>
                                {selectedLibraryConfigs.map((config) => {
                                    const item = contentItems.find(c => c.id === config.content_item_id);
                                    if (!item) return null;
                                    const isTracker = item.content_type === 'activity_log_request';

                                    return (
                                        <ListItem
                                            key={item.id}
                                            divider
                                            secondaryAction={
                                                <Tooltip title="Configure Settings">
                                                    <IconButton edge="end" onClick={(e) => handleMenuOpen(e, config)}>
                                                        <SettingsIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                        >
                                            <ListItemText 
                                                primary={
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {item.title} {isTracker && <Chip label="Tracker" size="small" color="primary" variant="outlined" sx={{ml:1, height: 20}}/>}
                                                    </Typography>
                                                } 
                                                secondary={
                                                    <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                                        {config.is_critical && <Chip icon={<ReportProblemIcon />} label="Critical" color="error" size="small" variant="outlined" />}
                                                        {config.require_attestation && <Chip icon={<CheckCircleIcon />} label="Attestation" color="info" size="small" variant="outlined" />}
                                                        <Typography variant="caption" color="text.secondary">Freq: {config.frequency}</Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Paper>
                    )}
                </Grid>

                {/* --- 3. Standard Check-ins (System Templates) --- */}
                <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="Daily Activities & Tracking" /></Divider></Grid>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>3. Standard Check-ins</Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <FormGroup row>
                            {systemCheckins.map(item => (
                                <FormControlLabel 
                                    key={item.id} 
                                    control={<Checkbox checked={selectedCheckinIds.includes(item.id)} onChange={() => handleCheckinChange(item.id)} />} 
                                    label={<Typography variant="body2">{item.title}</Typography>} 
                                    sx={{ width: '30%', mr: 0, mb: 1 }}
                                />
                            ))}
                        </FormGroup>
                    </Paper>
                </Grid>
                
                {/* --- 4. Alert Rules --- */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>4. Alert Rules (Auto-Generated & Custom)</Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        {alertRules.map((rule) => (
                            <Grid container spacing={1} key={rule.id} alignItems="center" sx={{ mb: 2 }}>
                                {rule.type === 'realtime_threshold' ? (<><Grid item xs={12} sm={6}><Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.desc}</Typography><Typography variant="caption">{rule.parameter}</Typography></Grid><Grid item xs={4} sm={2}><Typography variant="body2" sx={{ textAlign: 'center' }}>{rule.condition}</Typography></Grid><Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Value" value={rule.value} onChange={e => handleRuleChange(rule.id, 'value', e.target.value)} /></Grid></>) 
                                : (<Grid item xs={12} sm={11}><TextField fullWidth multiline rows={2} size="small" label="AI Longitudinal Pattern Prompt" value={rule.ai_prompt} onChange={e => handleRuleChange(rule.id, 'ai_prompt', e.target.value)} /></Grid>)}
                                <Grid item xs={2} sm={1} sx={{textAlign: 'right'}}><Tooltip title="Delete Rule"><IconButton onClick={() => deleteRule(rule.id)} size="small"><DeleteOutlineIcon /></IconButton></Tooltip></Grid>
                            </Grid>
                        ))}
                        <Button startIcon={<PsychologyIcon />} onClick={addNewAIPrompt} size="small">Add AI Pattern Rule</Button>
                    </Paper>
                </Grid>
            </Grid>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleToggleConfig('is_critical')}>
                    <ListItemIcon>
                        {currentItemForMenu?.is_critical ? <CheckCircleIcon fontSize="small" color="success"/> : <ReportProblemIcon fontSize="small" color="error"/>}
                    </ListItemIcon>
                    Mark as Critical
                </MenuItem>
                <MenuItem onClick={() => handleToggleConfig('require_attestation')}>
                     <ListItemIcon>
                        {currentItemForMenu?.require_attestation ? <CheckCircleIcon fontSize="small" color="success"/> : <ReportProblemIcon fontSize="small" />}
                    </ListItemIcon>
                    Require Patient Attestation
                </MenuItem>
            </Menu>

            <Box sx={{ mt: 4, textAlign: 'right' }}>
                <Button variant="contained" onClick={handleSaveBundle}>Save Care Bundle</Button>
            </Box>
        </Paper>
    );
};
export default CareBundleBuilder;